// `extension-alert-entities-profile-actions` — the row of icons under a candidate's name.
//
// The overlay at the top of the upstream template (`extension-alert-profile-action-overlay`,
// a spinner and an OK button) is NOT part of this component's own view: it is the body of a
// modal the action callbacks open, sharing the file only because the two were written
// together. It lands with the actions that raise it, not here.
import { useState, type ReactElement } from 'react'
import {
  inlineProfileActions,
  overflowProfileActions,
  showActionsDropdown,
  type ProfileActionDefinition,
  type ProfileActionEntity,
} from './profileActions.ts'

export interface ProfileActionsStripProps {
  entity: ProfileActionEntity
  actions: readonly ProfileActionDefinition[]
  /** The profile header passes 4; see `inlineActionCount` for why five icons still fit. */
  maxActionAmount?: number
  isDropdownEnabled?: boolean
  onAction?: (action: ProfileActionDefinition) => void
}

export function ProfileActionsStrip({
  entity,
  actions,
  maxActionAmount = 4,
  isDropdownEnabled = false,
  onAction,
}: ProfileActionsStripProps): ReactElement {
  const [open, setOpen] = useState(false)
  const inline = inlineProfileActions(actions, maxActionAmount, isDropdownEnabled, entity)
  const overflow = overflowProfileActions(actions, maxActionAmount, isDropdownEnabled, entity)

  return (
    <div className="extension-alert-candidates-profile-actions">
      {inline.map((action) => (
        <div
          key={action.name}
          // The name is IN the class — `extension-alert-candidates-profile-action-add-to-job`
          // is a real selector, which is why `ProfileAction`'s values are kebab-case.
          className={`extension-alert-candidates-profile-action cursor-pointer extension-alert-candidates-profile-action-${action.name}${
            action.isOn?.(entity) === true ? ' on' : ''
          }`}
          title={action.getTitle(entity)}
          role="button"
          tabIndex={0}
          onClick={() => {
            onAction?.(action)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') onAction?.(action)
          }}
        >
          <i className={action.getIcon(entity)} />
        </div>
      ))}
      {!showActionsDropdown(actions, isDropdownEnabled, entity) ? null : (
        <div className="candidate-actions-dropdown cursor-pointer">
          <div className={`actions-dropdown-caret${open ? ' open' : ''}`}>
            <i
              className="far fa-ellipsis-v"
              title="More actions"
              role="button"
              tabIndex={0}
              onClick={() => {
                setOpen(!open)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') setOpen(!open)
              }}
            />
            <ul className="dropdown-menu dropdown-menu-right" role="menu">
              {overflow.map((action) => (
                // `slim-view-<name>-all` — the same second class the bulk overflow menu uses.
                <li key={action.name} role="menuitem" className={`candidate-action-more slim-view-${action.name}-all`}>
                  <a
                    href="#"
                    title={action.getTitle(entity)}
                    onClick={(event) => {
                      event.preventDefault()
                      onAction?.(action)
                    }}
                  >
                    <i className={action.getIcon(entity)} />
                    &nbsp;&nbsp; {action.getTitle(entity)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
